import { useState, useEffect } from 'react';
import { publicApi } from '@/lib/api';
import { FadeIn } from '@/components/FadeIn';
import { useSiteContent, useContentValue } from '@/contexts/SiteContentContext';
import { MapPin, Phone, Clock, Mail, Loader2 } from 'lucide-react';

export default function ContactSection() {
  const { contact } = useSiteContent();
  const c = useContentValue();

  if (!contact) return <section id="kontakt" className="py-24"><div className="flex justify-center"><Loader2 size={32} className="text-amber-400 animate-spin" /></div></section>;

  const blocks = [
    { icon: MapPin, label: 'Adresse', lines: [contact?.address_line1, contact?.address_line2].filter(Boolean) },
    { icon: Phone, label: 'Telefon', lines: [contact?.phone].filter(Boolean) },
    { icon: Mail, label: 'E-Mail', lines: [contact?.email].filter(Boolean) },
    { icon: Clock, label: 'Öffnungszeiten', lines: (contact?.hours || []).filter(h => h.active).map(h => `${h.day}: ${h.open} – ${h.close}`).concat((contact?.hours || []).filter(h => !h.active).map(h => `${h.day}: geschlossen`)) },
  ];

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1C1C1B]/30 to-transparent pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase font-body">{c('Kontakt.Eyebrow', 'Kontakt')}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-100 mt-3 tracking-wide uppercase">{c('Kontakt.Überschrift', 'Besuch uns')}</h2>
            <p className="text-stone-400 text-base max-w-lg mx-auto mt-4 font-body">{c('Kontakt.Beschreibung', 'Wir freuen uns auf dich. Komm vorbei oder ruf uns an.')}</p>
          </div>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <FadeIn delay={100}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {blocks.map((info) => (
                <div key={info.label} data-testid={`contact-${info.label.toLowerCase()}`} className="glass-card rounded-xl p-6 hover:border-amber-400/20 transition-all duration-300">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                    <info.icon size={18} className="text-amber-400" />
                  </div>
                  <h3 className="text-stone-100 font-body font-semibold text-sm mb-2">{info.label}</h3>
                  {info.lines.map((line, i) => <p key={i} className="text-stone-400 text-sm font-body leading-relaxed">{line}</p>)}
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={200}>
            <div
              data-testid="contact-map"
              className="rounded-xl overflow-hidden h-full min-h-[320px]"
            >
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent([contact?.address_line1, contact?.address_line2].filter(Boolean).join(', ') || 'Gasshof 2, 6014 Luzern')}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '320px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="TreffPunkt Standort"
                className="w-full h-full"
              />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
